import { expVal } from '@confluence/feature-experiments';

import { resolveConfluenceDraftSiblingParent } from '@atlassian/conversation-assistant-utils/resolveConfluenceDraftSiblingParent';
import type { DuplicateMauiAppSuccess } from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions/duplicate-maui-app';

type DuplicateMauiApp = (
	appId: string,
	containerContentId: string,
	contentType?: string,
) => Promise<DuplicateMauiAppSuccess>;

type RovoConversationContext = {
	conversationId: string;
	messageId: string;
	invocationId: string;
};

type MauiEmbedTargetSpace = {
	id: string;
	key: string;
	parentId: string;
};

type CreateConfluenceMauiDiagramPasteConfigOptions = {
	contentId: string | undefined;
	contentType?: string;
	duplicateMauiApp?: DuplicateMauiApp;
	siteId: string;
	spaceKey: string;
	userId?: string;
};

type CreateConfluenceMauiDiagramPasteConfigResult = {
	getCurrentPageId: () => string | undefined;
	getCurrentContentType: () => string | undefined;
	duplicateMauiApp: DuplicateMauiApp | undefined;
	finalizeLocation: {
		currentParentContent:
			| {
					contentType: string;
					id: string;
					title: string;
			  }
			| undefined;
		currentSpace: {
			id: string;
			key: string;
			name: string;
			canCreatePage: boolean;
		};
		siteId: string;
		userId: string | undefined;
	};
	finalizeAfterMove: (
		mauiContentId: string,
		parentPageId: string,
		conversationContext?: RovoConversationContext,
		targetSpace?: MauiEmbedTargetSpace,
	) => Promise<void>;
	isFinalizePermissionError: (error: Error) => boolean;
	resolveContentSpaceKey: (mauiContentId: string) => Promise<string | null>;
	targetSpaceKey: string;
};

const PERMISSION_ERROR_SUBSTRING = 'can not create in space';

class PermissionError extends Error {
	public readonly isPermissionError = true;

	constructor(message: string) {
		super(message);
		this.name = 'PermissionError';
	}
}

async function buildErrorFromResponse(message: string, response: Response): Promise<Error> {
	const messageWithStatus = `${message} with status ${response.status}`;
	if (response.status === 400) {
		const rawText = await response.text().catch(() => '');
		if (rawText.toLowerCase().includes(PERMISSION_ERROR_SUBSTRING)) {
			return new PermissionError(messageWithStatus);
		}
	}
	return new Error(messageWithStatus);
}

export function createConfluenceMauiDiagramPasteConfig({
	contentId,
	contentType,
	duplicateMauiApp,
	siteId,
	spaceKey,
	userId,
}: CreateConfluenceMauiDiagramPasteConfigOptions): CreateConfluenceMauiDiagramPasteConfigResult {
	return {
		getCurrentPageId: (): string | undefined => contentId,
		getCurrentContentType: (): string | undefined => contentType,
		duplicateMauiApp,
		finalizeLocation: {
			currentParentContent: contentId
				? {
						contentType: contentType ?? 'page',
						id: contentId,
						title: '',
					}
				: undefined,
			currentSpace: {
				id: spaceKey,
				key: spaceKey,
				name: spaceKey,
				canCreatePage: false,
			},
			siteId,
			userId,
		},
		finalizeAfterMove: async (
			mauiContentId: string,
			parentPageId: string,
			conversationContext?: RovoConversationContext,
			targetSpace?: MauiEmbedTargetSpace,
		): Promise<void> => {
			const resolvedSpaceKey = targetSpace?.key ?? spaceKey;
			const targetSpaceId = targetSpace?.id;

			// Drafts can't have children; reparent as a sibling when possible.
			let resolvedParentId = targetSpace?.parentId ?? parentPageId;
			if (
				!targetSpace?.parentId &&
				!expVal<boolean>('confluence_maui_finalize_diagram_sibling_draft', 'value', false)
			) {
				try {
					const ancestorId = await resolveConfluenceDraftSiblingParent(parentPageId);
					if (ancestorId) {
						resolvedParentId = ancestorId;
					}
				} catch {
					// Swallow — finalize continues with the original parent.
				}
			}

			const monolithBody = {
				id: mauiContentId,
				type: 'whiteboard',
				spaceKey: resolvedSpaceKey,
				parentId: resolvedParentId,
				...(targetSpaceId ? { spaceId: targetSpaceId } : {}),
			};

			// Use the convo-ai path when a conversationContext (_rc) is present.
			// Fall back to monolith when no _rc is available.
			if (conversationContext) {
				// Fetch messages to reconstruct the full action payload; _rc only carries IDs.
				let matchedAction: Record<string, unknown> | undefined;

				const messagesResponse = await fetch(
					`/gateway/api/assist/rovo/v1/chat/conversation/${conversationContext.conversationId}/messages`,
					{
						method: 'GET',
						credentials: 'include',
						headers: {
							'x-product': 'confluence',
							'x-experience-id': 'ai-mate',
						},
					},
				);

				if (messagesResponse.ok) {
					const messagesPayload = await messagesResponse.json();
					const messages = Array.isArray(messagesPayload?.items) ? messagesPayload.items : [];

					for (const message of messages) {
						if (message?.id !== conversationContext.messageId) {
							continue;
						}
						const actions = Array.isArray(message?.actions) ? message.actions : [];
						matchedAction = actions.find(
							(action: Record<string, unknown>) =>
								action?.invocationId === conversationContext.invocationId,
						);
						if (matchedAction) {
							break;
						}
					}
				}

				if (!matchedAction) {
					if (expVal<boolean>('maui-native-embed-finalize-diagram-anywhere', 'value', false)) {
						throw new Error('Cannot finalize: conversation action data is unavailable.');
					}
					// Fall through to monolith path below.
				} else {
					const existingData =
						(matchedAction.data as { metadata?: Record<string, unknown> } | undefined) ?? {};
					const existingMetadata = existingData.metadata ?? {};

					const actionResponse = await fetch(
						`/gateway/api/assist/rovo/v1/chat/conversation/${conversationContext.conversationId}/action`,
						{
							method: 'POST',
							credentials: 'include',
							headers: {
								'Content-Type': 'application/json;charset=UTF-8',
								'x-product': 'confluence',
								'x-experience-id': 'ai-mate',
							},
							body: JSON.stringify({
								context: {
									...matchedAction,
									messageId: conversationContext.messageId,
									status: 'pending',
									resolution: {
										type: 'confirm',
										message: 'finalize',
									},
									data: {
										...existingData,
										metadata: {
											...existingMetadata,
											contentId: mauiContentId,
											parentId: resolvedParentId,
											spaceKey: resolvedSpaceKey,
											...(targetSpaceId ? { spaceId: targetSpaceId } : {}),
										},
									},
								},
							}),
						},
					);
					if (!actionResponse.ok) {
						// Propagate convo-ai failures (incl. permission errors) so the
						// space-picker recovery flow can react correctly.
						throw await buildErrorFromResponse('Convo-ai finalize action failed', actionResponse);
					}
					return;
				}
			} else if (expVal<boolean>('maui-native-embed-finalize-diagram-anywhere', 'value', false)) {
				throw new Error('Cannot finalize: no conversation context found in the embed link.');
			}

			// Monolith fallback — works with contentId alone, no conversation data needed.
			const monolithResponse = await fetch(
				`/gateway/api/ex/confluence/${siteId}/wiki/rest/internals/latest/smart-create/finalize`,
				{
					method: 'PUT',
					credentials: 'include',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(monolithBody),
				},
			);
			if (!monolithResponse.ok) {
				throw await buildErrorFromResponse('Monolith finalize failed', monolithResponse);
			}
		},
		isFinalizePermissionError: (error: Error): boolean =>
			error instanceof PermissionError && error.isPermissionError,
		resolveContentSpaceKey: async (mauiContentId: string): Promise<string | null> => {
			try {
				const response = await fetch(`/wiki/rest/api/content/${mauiContentId}?expand=space`, {
					credentials: 'include',
				});
				if (!response.ok) {
					return null;
				}
				const data = await response.json();
				return data?.space?.key ?? null;
			} catch {
				return null;
			}
		},
		targetSpaceKey: spaceKey,
	};
}
